/**
 * Item page: which cycle this page is showing (roadmap S-37).
 *
 * An item's rank, and what may be changed on it, are relative to a cycle. This
 * band says which cycle the page was opened from, that cycle's rank and rank
 * mode, and what other cycles fixed, as facts and not as locks. With no cycle
 * named it says what the page is showing instead, and lets the person pick one.
 */

import { Link } from 'react-router-dom';
import { LABEL_MONO } from '@/components/mono/primitives';
import { RANK_MODE_LABEL, cycleInContext, rankOfRecordFact, type ItemCycleStanding, type ItemPlanHold } from '@/lib/itemCycleStanding';

function factText(p: ItemPlanHold, itemVersion: number | null): string {
  const fact = rankOfRecordFact(p);
  if (p.version == null || itemVersion == null || p.version >= itemVersion) return `${fact}.`;
  return `${fact}. That plan keeps version ${p.version} until it is amended; this is version ${itemVersion}.`;
}

export function ItemCycleBand({
  standing,
  cycleNamed,
  rankText,
  lockingPlanName,
  facts,
  onPick,
}: {
  standing: ItemCycleStanding;
  /** A cycle was named in the link, whether or not the item is in it. */
  cycleNamed: boolean;
  /** The item's rank in the cycle shown, in words. */
  rankText: string | null;
  /** The plan locking this page, when one does. */
  lockingPlanName: string | null;
  /** What plans other than the locking one hold: facts. */
  facts: ItemPlanHold[];
  onPick: (cycleId: string | null) => void;
}) {
  const cycle = cycleInContext(standing);
  const showing = cycle?.id ?? null;
  if (standing.cycles.length < 2 && !cycleNamed && facts.length === 0) return null;

  const seg = (on: boolean) =>
    `border px-2.5 py-1 text-xs ${on ? 'relative z-10 border-[var(--slate)] bg-[var(--slate-dim)] font-medium text-[var(--text-pri)]' : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'}`;

  let line: string;
  if (cycle) {
    const mode = cycle.mode === 'ranking' ? 'still being ranked' : cycle.mode === 'in_execution' ? 'in execution' : 'closed';
    line = `${cycle.name} is ${mode}.${rankText ? ` Here the item is ${rankText}.` : ''}`;
    if (cycle.mode === 'ranking' && facts.length > 0) line += ' A change made here makes a new version of the item.';
  } else if (standing.cycle) {
    line = `This item is not in ${standing.cycle.name}. This page shows ${lockingPlanName ?? 'every cycle together'}.`;
  } else {
    line = `No cycle was named, so this page shows ${lockingPlanName ?? 'every cycle together'}. Pick a cycle to see its rank and what it locks.`;
  }

  return (
    <div className="mt-4 border border-[var(--rule)] bg-[var(--surface)] px-4 py-3" data-testid="item-cycle-band" data-cycle={showing ?? ''} data-mode={cycle?.mode ?? ''}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={LABEL_MONO}>{cycle ? `Opened from ${cycle.name} · ${RANK_MODE_LABEL[cycle.mode]}` : 'Standing'}</div>
          <p className="mt-1 text-sm text-[var(--text-pri)]" data-testid="item-cycle-line">
            {line}
          </p>
          {facts.map((p) => (
            <p key={p.planCycleId} className="mt-1 text-sm text-[var(--text-sec)]" data-testid="item-cycle-fact">
              <Link to={`/planning/plans/${p.planCycleId}`} className="underline">
                {p.planName}
              </Link>
              {p.cycleName ? ` (${p.cycleName})` : ''}: {factText(p, standing.latestVersion)}
            </p>
          ))}
        </div>
        {standing.cycles.length > 0 && (
          <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Which cycle">
            <button type="button" role="radio" aria-checked={showing === null} onClick={() => onPick(null)} className={seg(showing === null)}>
              All cycles
            </button>
            {standing.cycles.map((c) => (
              <button key={c.id} type="button" role="radio" aria-checked={c.id === showing} onClick={() => onPick(c.id)} className={seg(c.id === showing)}>
                {c.name} · {RANK_MODE_LABEL[c.mode]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
